import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";

export const RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME =
  "epocus-referral-outcome";

export const useRxCollectionEpocusReferralOutcome =
  (): RxCollection<EpocusReferralOutcome> | null =>
    useRxCollection<EpocusReferralOutcome>(
      RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME,
    );

type UseRxData = Parameters<typeof useRxData<EpocusReferralOutcome>>;
export type EpocusReferralOutcomeQueryConstructor = UseRxData["1"];

export type EpocusReferralDiagnosisConfirmed = "yes" | "no" | "unable";
export type EpocusReferralActionTaken =
  | "admitted"
  | "treatedDischarged"
  | "referredOnward"
  | "noAction";

const EPOCUS_REFERRAL_OUTCOME_SCHEMA_LITERAL = {
  title: "epocus referral outcome schema",
  version: 1,
  description: "Outcome submitted by the receiving clinician for a referral",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    referralId: {
      type: "string",
      maxLength: 100,
    },
    recordedByUserId: {
      type: "string",
      maxLength: 256,
    },
    patientSeen: {
      type: "boolean",
    },
    dateSeen: {
      type: "string",
      maxLength: 30,
    },
    diagnosisConfirmed: {
      type: "string",
      enum: ["yes", "no", "unable"],
      maxLength: 10,
    },
    actionTaken: {
      type: "string",
      enum: ["admitted", "treatedDischarged", "referredOnward", "noAction"],
      maxLength: 20,
    },
    clinicalNotes: {
      type: ["string", "null"],
    },
    createdAt: {
      type: "string",
      format: "date-time",
      maxLength: 30,
    },
  },
  required: [
    "id",
    "referralId",
    "recordedByUserId",
    "patientSeen",
    "dateSeen",
    "diagnosisConfirmed",
    "actionTaken",
    "createdAt",
  ],
  indexes: [["referralId"]],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_REFERRAL_OUTCOME_SCHEMA_LITERAL);

export type EpocusReferralOutcome = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const EPOCUS_REFERRAL_OUTCOME_SCHEMA: RxJsonSchema<EpocusReferralOutcome> =
  EPOCUS_REFERRAL_OUTCOME_SCHEMA_LITERAL;
